import {useState, useEffect, useCallback, useMemo} from 'react';
import Duration from '@/types/models/pricing/duration';
import {SubscriptionPlan} from '@/types/models/pricing/subscriptionPlan';
import {splitPlansByDuration} from '@/views/content-studio/pricing/utils';
import {Option} from '@/components/dropdown-button/DropdownButton';
import useHandlePricingCtaClick from '@/hooks/use-handle-pricing-cta-click';

type UsePricingPlanSelectionProps = {
  plans: SubscriptionPlan[];
  activeSubscriptionId?: string;
  activePlan?: SubscriptionPlan;
  shouldSkipUpgradeModal: boolean;
  sendUiInteractionEvent: (buttonName: string) => void;
  sendPurchaseProcessStartedEvent: (
    product_id: string,
    process_id: string
  ) => void;
  navigateToWebPayments?: () => void;
  isOnLaunch?: boolean;
  onLaunchCallback?: () => void;
  enforcePaymentManagerRole?: boolean;
  isTrialFeatureEnabled?: boolean;
};

export function usePricingPlanSelection({
  plans,
  activeSubscriptionId,
  activePlan,
  shouldSkipUpgradeModal,
  sendUiInteractionEvent,
  sendPurchaseProcessStartedEvent,
  isOnLaunch,
  onLaunchCallback,
  enforcePaymentManagerRole,
  isTrialFeatureEnabled = false,
}: UsePricingPlanSelectionProps) {
  const {yearlyPlans, monthlyPlans} = useMemo(
    () => splitPlansByDuration(plans),
    [plans]
  );

  const {handleCtaClick} = useHandlePricingCtaClick({
    shouldSkipUpgradeModal,
    sendUiInteractionEvent,
    sendPurchaseProcessStartedEvent,
    enforcePaymentManagerRole,
  });

  // The default selected plan to one higher than the active one
  const getDefaultPlan = useCallback(() => {
    if (activePlan) {
      const activeList =
        activePlan.duration === Duration.MONTHLY ? monthlyPlans : yearlyPlans;
      const defaultPlan = activeList.find(
        (p) => !p.isDisabled && !(p.id === activePlan.id)
      );
      if (defaultPlan) {
        return defaultPlan;
      }
    }
    return monthlyPlans[0] || undefined;
  }, [activePlan, monthlyPlans, yearlyPlans]);

  const [selectedSubscriptionPlan, setSelectedSubscriptionPlan] = useState<
    SubscriptionPlan | undefined
  >(getDefaultPlan());

  useEffect(() => {
    const newDefaultPlan = getDefaultPlan();
    if (newDefaultPlan && newDefaultPlan.id !== selectedSubscriptionPlan?.id) {
      setSelectedSubscriptionPlan(newDefaultPlan);
    }
  }, [getDefaultPlan]);

  const selectedDuration: Duration =
    selectedSubscriptionPlan?.duration || Duration.MONTHLY;
  const presentedPlanList =
    selectedDuration === Duration.MONTHLY ? monthlyPlans : yearlyPlans;

  const handleDurationToggled = useCallback(() => {
    const matchingPlan =
      selectedDuration === Duration.YEARLY
        ? monthlyPlans.find((p) => p.tier === selectedSubscriptionPlan?.tier) ||
          monthlyPlans[0]
        : yearlyPlans.find((p) => p.tier === selectedSubscriptionPlan?.tier) ||
          yearlyPlans[0];

    if (matchingPlan) {
      sendUiInteractionEvent(matchingPlan.duration.valueOf());
      setSelectedSubscriptionPlan(matchingPlan);
    }
  }, [
    selectedSubscriptionPlan,
    monthlyPlans,
    yearlyPlans,
    sendUiInteractionEvent,
  ]);

  const handlePlanSelected = useCallback(
    (option: Option) => {
      const newSelectedPlan = presentedPlanList?.find(
        (plan) => plan.id === option.id
      );
      if (newSelectedPlan) {
        sendUiInteractionEvent(option.analyticsName || '');
        setSelectedSubscriptionPlan(newSelectedPlan);
      }
    },
    [selectedSubscriptionPlan, sendUiInteractionEvent]
  );

  const handleSubscribeButtonClick = useCallback(
    (buttonName: string) => {
      if (isOnLaunch && onLaunchCallback) {
        onLaunchCallback();
      }
      if (selectedSubscriptionPlan) {
        handleCtaClick(
          activeSubscriptionId,
          activePlan,
          selectedSubscriptionPlan,
          buttonName,
          isTrialFeatureEnabled
        );
      }
    },
    [selectedSubscriptionPlan, handleCtaClick, activeSubscriptionId, activePlan]
  );

  const getOriginalPrice = useCallback(
    () =>
      selectedDuration === Duration.YEARLY
        ? monthlyPlans.find((p) => p.tier === selectedSubscriptionPlan?.tier)
            ?.price
        : undefined,
    [selectedSubscriptionPlan, monthlyPlans]
  );

  return {
    selectedSubscriptionPlan,
    selectedDuration,
    presentedPlanList,
    yearlyPlans,
    monthlyPlans,
    handleDurationToggled,
    handlePlanSelected,
    handleSubscribeButtonClick,
    getOriginalPrice,
  };
}

export default usePricingPlanSelection;
